import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { paymentByIdApi } from "../../../services/billing.service";
import { toast } from "react-toastify";
import { useGetApi } from "../../../hooks/useGetApi";
import { getTransactionTypeApi } from "../../../services/transaction-type.service";

const PaymentModal = ({ open, onClose, id, refetch }) => {
  const initialState = {
    transactionType: null,
    transactionId: null,
    amountPaid: 0,
    remarks: "To Reception",
    paymentType: "",
  };
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialState);
  const [confirmAmountPaid, setConfirmAmountPaid] = useState(0);

  const { dataList: transactionList } = useGetApi({
    apiFunction: getTransactionTypeApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
  });

  const isPendingPayment = formData?.transactionType?.transactionMode === "Pending Payment";
  const amountsMatch = isPendingPayment 
    ? (Number(confirmAmountPaid) >= 0 && Number(confirmAmountPaid) <= Number(formData?.amountPaid))
    : formData?.amountPaid === Number(confirmAmountPaid);

  const transactionTypeList = [
    ...(transactionList?.find((option) => option?.transactionMode === "Payment")?.transactionTypes || []),
    ...(transactionList?.find((option) => option?.transactionMode === "Pending Payment")?.transactionTypes || [])
  ];

  const handleClose = () => {
    onClose();
    setFormData(initialState);
    setConfirmAmountPaid(0);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const parsedValue =
      type === "checkbox" ? checked : type === "number" ? Number(value) : value;

    setFormData((preValue) => ({
      ...preValue,
      [name]: parsedValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amountsMatch) {
      toast.error("Amount and Confirm Amount doesn't match.");
      return;
    }
    const payload = {
      ...formData,
      transactionType:
        typeof formData?.transactionType === "object"
          ? formData?.transactionType?._id
          : formData?.transactionType,
    };

    setLoading(true);
    const response = await paymentByIdApi(id, payload);
    setLoading(false);

    if (response.code === 200 || response.code === 201) {
      toast.success("Payment added successfully");
      handleClose();
      refetch();
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            maxWidth: "unset",
            width: { xs: "95vw", lg: "1100px" },
            bgcolor: "#E5F4FF",
            borderRadius: "20px",
          },
        },
      }}
    >
      <DialogContent>
        {/* dialog title */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: "20px",
          }}
        >
          <Typography variant="h6" sx={{ color: "#5D6A82" }}>
            {"Add Payment"}
          </Typography>
          <Close
            onClick={handleClose}
            sx={{ cursor: "pointer", color: "#5D6A82" }}
          />
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          {/* Transaction Details */}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={transactionTypeList || []}
                getOptionLabel={(option) =>
                  `${option?.name || ""} (${option?.paymentMethod})`
                }
                renderInput={(params) => (
                  <TextField {...params} label="Transaction Type" required />
                )}
                value={formData?.transactionType || null}
                onChange={(_, newValue) => {
                  handleInputChange({
                    target: {
                      name: "transactionType",
                      value: newValue,
                    },
                  });
                }}
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl
                sx={{ minWidth: "100px", bgcolor: "white" }}
                fullWidth
                required
              >
                <InputLabel id="payment-type-id">Payment Type</InputLabel>
                <Select
                  labelId="payment-type-id"
                  label="Payment Type"
                  name="paymentType"
                  value={formData?.paymentType || ""}
                  onChange={handleInputChange}
                >
                  <MenuItem value="InvoicePayment">Invoice Payment</MenuItem>
                  <MenuItem value="SelfPayment">Self Payment</MenuItem>
                  <MenuItem value="Refund">Refund</MenuItem>
                  <MenuItem value="IPDAdvance">IPD Advance</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              {formData?.transactionType?.name === "QR Payment" ? (
                <TextField
                  label="Transaction ID"
                  name="transactionId"
                  value={formData?.transactionId || ""}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  sx={{ bgcolor: "white" }}
                />
              ) : (
                <TextField
                  label="Transfer Bank A/C"
                  name="transferBankAccount"
                  value={
                    formData?.transactionType?.bankDetails?.find(
                      (bankDetail) => bankDetail?.default
                    )?.accountNumber || ""
                  }
                  slotProps={{ input: { readOnly: true } }}
                  fullWidth
                  required
                  sx={{ bgcolor: "white" }}
                />
              )}
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                type="number"
                label="Amount (₹)"
                name="amountPaid"
                value={formData?.amountPaid || ""}
                onChange={handleInputChange}
                fullWidth
                required
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                type="number"
                label="Confirm Amount (₹)"
                name="confirmAmountPaid"
                value={confirmAmountPaid || ""}
                onChange={(e) =>
                  setConfirmAmountPaid(Number(e.target.value) || 0)
                }
                fullWidth
                required
                error={!amountsMatch && confirmAmountPaid !== ""}
                helperText={
                  !amountsMatch && confirmAmountPaid !== ""
                    ? isPendingPayment 
                      ? `Amount must be between ₹0 and ₹${formData?.amountPaid}`
                      : "Amount does not match!"
                    : ""
                }
                sx={{ bgcolor: "white" }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Remarks"
                name="remarks"
                value={formData?.remarks || ""}
                onChange={handleInputChange}
                fullWidth
                required
                sx={{ bgcolor: "white" }}
              />
            </Grid>
          </Grid>

          {/* submit button */}
          <Box sx={{ my: "20px", textAlign: "center" }}>
            <Button
              type="submit"
              variant="contained"
              sx={{ width: "150px" }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "Submit"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;
